import Component from '@glimmer/component';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { t } from 'ember-intl';
import { dropTask } from 'ember-concurrency';
import { divide } from 'tio-ui/math';
import { formatCurrency } from 'tio-ui/format';
import Button from 'tio-ui/components/button';
import Modal from 'tio-ui/components/modal';
import { PhCheck } from 'ember-phosphor-icons';
import { saveRecord } from 'tio-data/requests/save-record';
import LpnSurveyCard from 'tio-employee/components/tas/applications/lpn-survey-card';
import type { RadioOption } from 'tio-ui/components/radio-group';
import type IntlService from 'ember-intl/services/intl';
import type { Store as StoreService } from '@warp-drive/core';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';

interface SubmittedModalSignature {
  Args: {
    application: TasApplicationModel;
    isOpen: boolean;
    onClose: () => void;
    /** Resolved by the manage route via utils/tas/lpn-survey, and only when
     * arriving submitted — the confirmation page gates the survey the same way. */
    showLpnSurvey?: boolean;
  };
}

/**
 * The post-submission receipt, shown over the manage screen instead of routing to
 * the standalone `/confirmation` page (#10521).
 *
 * Opened by the `applicationSubmitted` query param the guided wizard sets on its way
 * here; closing clears that param. Everything it displays is derived from the
 * application record the route already loaded — nothing rides in the URL, which
 * would be a second source of truth, forgeable, and would put course and
 * scholarship names into browser history and server logs.
 *
 * The amounts come from the same model getters the manage page's own Request
 * Summary section reads (`courseTuition`, `scholarshipsTotal`, `requestedTotal`),
 * so the modal and the page underneath it cannot disagree about the numbers. The
 * markup is separate because the compositions differ — this one carries the total
 * in its header and a scholarship row, and its course names are not drawer
 * triggers.
 */
export default class SubmittedModal extends Component<SubmittedModalSignature> {
  @service declare intl: IntlService;
  @service declare store: StoreService;

  @tracked selectedLpnOption?: string;
  @tracked lpnSurveySubmitted = false;

  get courses() {
    return this.args.application.activeCourses ?? [];
  }

  get scholarshipsTotal() {
    return this.args.application.scholarshipsTotal ?? 0;
  }

  get hasScholarships() {
    return this.scholarshipsTotal > 0;
  }

  get requestedTotal() {
    return this.args.application.requestedTotal ?? 0;
  }

  /** Same two options the confirmation page offers, so the answers are comparable. */
  get lpnSurveyOptions(): RadioOption[] {
    return [
      { key: 'yes', label: this.intl.t('tuition_assistance.submitted.lpn_survey.got_discount') },
      { key: 'no', label: this.intl.t('tuition_assistance.submitted.lpn_survey.no_discount') },
    ];
  }

  setLpnOption = (value: string) => {
    this.selectedLpnOption = value;
  };

  /** Mirrors the confirmation page's task: the answer is a field on the
   * application, so it persists against the same record either way. */
  submitLpnSurvey = dropTask(async () => {
    const { application } = this.args;
    application.lpnDiscountActivated = this.selectedLpnOption === 'yes';
    await saveRecord(this.store, application);
    this.lpnSurveySubmitted = true;
  });

  <template>
    {{! @ariaLabel because Modal wires only that arg to the dialog's accessible
      name — it does not associate the visible <h2> below, so without it a screen
      reader announces an unnamed dialog. }}
    <Modal
      @isOpen={{@isOpen}}
      @onClose={{@onClose}}
      @size="lg"
      @ariaLabel={{t "tuition_assistance.applications.manage.submitted.title"}}
      data-test-application-submitted-modal
    >
      <:body>
        <div class="tio-col items-center gap-4 text-center">
          {{! Brand-filled check with a soft ring, per the Figma spec. }}
          {{! h-/w- rather than size-24/size-16: the self-hosted sizing set only
            defines size-4/5/6/10/12, and reusing existing steps avoids adding CSS
            that would ship to every app. }}
          <div class="tio-row items-center justify-center h-24 w-24 rounded-full tio-bg-brand-soft">
            <div
              class="tio-row items-center justify-center h-16 w-16 rounded-full tio-bg-brand tio-fg-on-brand"
            >
              <PhCheck @size="36" @weight="bold" />
            </div>
          </div>

          <h2 class="tio-text-xl-semibold tio-fg-primary">
            {{t "tuition_assistance.applications.manage.submitted.title"}}
          </h2>
          <p class="tio-text-sm-normal tio-fg-secondary max-w-md">
            {{t "tuition_assistance.applications.manage.submitted.body"}}
          </p>
        </div>

        <div class="tio-col mt-6 gap-2">
          <h3 class="tio-text-sm-semibold tio-fg-primary">
            {{t "tuition_assistance.applications.manage.submitted.application_information"}}
          </h3>

          <section class="rounded-xl border tio-border-default p-4" data-test-submitted-summary>
            <div class="tio-row-between items-baseline gap-3 border-b tio-border-default pb-3">
              <h4 class="tio-text-sm-semibold tio-fg-primary">
                {{t "tuition_assistance.applications.manage.summary.heading"}}
              </h4>
              <span class="tio-text-sm-normal tio-fg-secondary">
                {{t
                  "tuition_assistance.applications.manage.summary.items"
                  count=this.courses.length
                }}
                •
                <span class="tio-text-sm-semibold tio-fg-primary" data-test-submitted-total>
                  {{formatCurrency (divide this.requestedTotal 100)}}
                </span>
              </span>
            </div>

            <ul class="tio-col gap-2 pt-3">
              {{#each this.courses as |course|}}
                <li class="tio-row-between gap-3" data-test-submitted-course>
                  <span class="tio-text-sm-normal tio-fg-info truncate min-w-0">
                    {{course.displayName}}
                  </span>
                  <span class="tio-text-sm-normal tio-fg-primary shrink-0">
                    {{formatCurrency (divide course.courseTuition 100)}}
                  </span>
                </li>
              {{/each}}

              {{#if this.hasScholarships}}
                <li
                  class="tio-row-between gap-3 border-t tio-border-default pt-2"
                  data-test-submitted-scholarship
                >
                  <span class="tio-text-sm-semibold tio-fg-primary">
                    {{t "tuition_assistance.applications.manage.submitted.scholarship"}}
                  </span>
                  <span class="tio-text-sm-normal tio-fg-primary shrink-0">
                    -{{formatCurrency (divide this.scholarshipsTotal 100)}}
                  </span>
                </li>
              {{/if}}
            </ul>
          </section>
        </div>

        {{#if @showLpnSurvey}}
          <div class="tio-row justify-center">
            <LpnSurveyCard
              @options={{this.lpnSurveyOptions}}
              @selected={{this.selectedLpnOption}}
              @onSelect={{this.setLpnOption}}
              @onSubmit={{this.submitLpnSurvey.perform}}
              @isSubmitting={{this.submitLpnSurvey.isRunning}}
              @submitted={{this.lpnSurveySubmitted}}
            />
          </div>
        {{/if}}

        <div class="tio-row mt-6 justify-center">
          <Button
            @appearance="outlined"
            class="w-40"
            {{on "click" @onClose}}
            data-test-submitted-modal-close
          >
            {{t "common.close"}}
          </Button>
        </div>
      </:body>
    </Modal>
  </template>
}
